import { useState } from 'react';
import { cn } from '../lib/utils';
import { Icon } from './ui/icons';

interface Control {
  id: string;
  code: string;
  title: string;
  description: string;
  category: string;
  framework: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'passing' | 'failing' | 'pending' | 'disabled';
  repositories_applied: number;
  repositories_total: number;
  last_run: string;
  pass_rate: number;
  evidence_required: boolean;
}

interface ControlsProps {
  navigate: (route: string) => void;
}

const mockControls: Control[] = [
  {
    id: '1',
    code: 'AC-1',
    title: 'Authentication and Access Control',
    description: 'Implement multi-factor authentication for all user accounts',
    category: 'Access Control',
    framework: 'SOC2',
    severity: 'critical',
    status: 'passing',
    repositories_applied: 8,
    repositories_total: 10,
    last_run: '2024-05-22T10:30:00Z',
    pass_rate: 95,
    evidence_required: true
  },
  {
    id: '2',
    code: 'SC-7',
    title: 'Boundary Protection',
    description: 'Monitor and control communications at external boundaries',
    category: 'System Communications',
    framework: 'ISO27001',
    severity: 'high',
    status: 'failing',
    repositories_applied: 6,
    repositories_total: 10,
    last_run: '2024-05-22T09:15:00Z',
    pass_rate: 67,
    evidence_required: true
  },
  {
    id: '3',
    code: 'AU-12',
    title: 'Audit Generation',
    description: 'Generate audit records for defined auditable events',
    category: 'Audit and Accountability',
    framework: 'SOC2',
    severity: 'medium',
    status: 'passing',
    repositories_applied: 9,
    repositories_total: 10,
    last_run: '2024-05-22T08:45:00Z',
    pass_rate: 89,
    evidence_required: false
  },
  {
    id: '4',
    code: 'IA-2',
    title: 'User Identification and Authentication',
    description: 'Uniquely identify and authenticate users',
    category: 'Identification and Authentication',
    framework: 'ISO27001',
    severity: 'critical',
    status: 'pending',
    repositories_applied: 5,
    repositories_total: 10,
    last_run: '2024-05-22T07:20:00Z',
    pass_rate: 0,
    evidence_required: true
  },
  {
    id: '5',
    code: 'CM-8',
    title: 'Information System Component Inventory',
    description: 'Develop and maintain an inventory of system components',
    category: 'Configuration Management',
    framework: 'SOC2',
    severity: 'medium',
    status: 'passing',
    repositories_applied: 10,
    repositories_total: 10,
    last_run: '2024-05-22T11:00:00Z',
    pass_rate: 100,
    evidence_required: false
  },
  {
    id: '6',
    code: 'SI-4',
    title: 'Information System Monitoring',
    description: 'Monitor the information system to detect attacks and indicators',
    category: 'System and Information Integrity',
    framework: 'ISO27001',
    severity: 'high',
    status: 'failing',
    repositories_applied: 4,
    repositories_total: 10,
    last_run: '2024-05-21T16:30:00Z',
    pass_rate: 40,
    evidence_required: true
  }
];

function ControlRow({ control, onClick }: { control: Control; onClick: () => void }) {
  const statusColors = {
    passing: 'text-success-600 bg-success-50',
    failing: 'text-danger-600 bg-danger-50',
    pending: 'text-warning-600 bg-warning-50',
    disabled: 'text-gray-600 bg-gray-50'
  };

  const severityColors = {
    critical: 'text-danger-600 bg-danger-50',
    high: 'text-warning-600 bg-warning-50',
    medium: 'text-primary-600 bg-primary-50',
    low: 'text-gray-600 bg-gray-50'
  };

  return (
    <tr 
      onClick={onClick}
      className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer transition"
    >
      <td className="py-4 pr-4 pl-5">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="font-data text-[12px] text-ink/60 bg-gray-100 px-2 py-1 rounded">
              {control.code}
            </span>
            {control.evidence_required && (
              <Icon name="file-text" size={14} className="text-warning-500" title="Evidence Required" />
            )}
          </div>
        </div>
      </td>
      <td className="px-3 py-4">
        <div>
          <div className="font-display font-semibold text-ink text-[14px] mb-1">{control.title}</div>
          <div className="font-display text-[12px] text-ink/60 line-clamp-2">{control.description}</div>
        </div>
      </td>
      <td className="px-3 py-4">
        <span className="font-data text-[12px] text-ink/70">{control.category}</span>
      </td>
      <td className="px-3 py-4">
        <span className={cn('px-2 py-1 rounded-full text-[11px] font-bold', severityColors[control.severity])}>
          {control.severity.toUpperCase()}
        </span>
      </td>
      <td className="px-3 py-4">
        <span className={cn('px-2 py-1 rounded-full text-[11px] font-bold', statusColors[control.status])}>
          {control.status.charAt(0).toUpperCase() + control.status.slice(1)}
        </span>
      </td>
      <td className="px-3 py-4">
        <div className="flex items-center gap-2">
          <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={cn(
                'h-full rounded-full',
                control.pass_rate >= 90 ? 'bg-success-500' :
                control.pass_rate >= 70 ? 'bg-warning-500' : 'bg-danger-500'
              )}
              style={{ width: `${control.pass_rate}%` }}
            />
          </div>
          <span className="font-display font-bold text-ink text-[13px] w-10 text-right">
            {control.pass_rate}%
          </span>
        </div>
      </td>
      <td className="px-3 py-4">
        <span className="font-data text-[13px] text-ink">
          {control.repositories_applied}/{control.repositories_total}
        </span>
      </td>
      <td className="pl-3 py-4">
        <Icon name="chevron-right" size={16} className="text-ink/40" />
      </td>
    </tr>
  );
}

export default function Controls({ navigate }: ControlsProps) {
  const [controls] = useState<Control[]>(mockControls);
  const [filter, setFilter] = useState<'all' | 'passing' | 'failing' | 'pending'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [frameworkFilter, setFrameworkFilter] = useState<string>('all');

  const categories = Array.from(new Set(controls.map(c => c.category)));
  const frameworks = Array.from(new Set(controls.map(c => c.framework)));

  const filteredControls = controls.filter(control => {
    const statusMatch = filter === 'all' || control.status === filter;
    const categoryMatch = categoryFilter === 'all' || control.category === categoryFilter;
    const frameworkMatch = frameworkFilter === 'all' || control.framework === frameworkFilter;
    return statusMatch && categoryMatch && frameworkMatch;
  });

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div>
          <h1 className="font-display font-bold text-ink" style={{ fontSize: 36, lineHeight: 1.05 }}>
            Controls
          </h1>
          <p className="font-display text-[18px] font-medium text-ink/70 mt-2">
            Track implementation status of security controls across frameworks.
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('controls/scan')}
            className="px-3 py-2 border border-gray-200 rounded-lg font-display text-[13px] font-medium text-ink/70 hover:border-ink/40 inline-flex items-center gap-2"
          >
            <Icon name="refresh" size={14} />
            Run All
          </button>
          
          <button
            onClick={() => navigate('controls/new')}
            className="h-10 px-4 rounded-[10px] bg-primary-500 text-white font-display font-bold text-[15px] inline-flex items-center gap-2 hover:bg-primary-600 transition"
          >
            <Icon name="plus" size={16} />
            Add Control
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Status Filter */}
        <div className="flex items-center gap-1 border-b border-gray-200">
          {[
            { id: 'all', label: 'All', count: controls.length },
            { id: 'passing', label: 'Passing', count: controls.filter(c => c.status === 'passing').length },
            { id: 'failing', label: 'Failing', count: controls.filter(c => c.status === 'failing').length },
            { id: 'pending', label: 'Pending', count: controls.filter(c => c.status === 'pending').length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setFilter(tab.id as any)}
              className={cn(
                'px-4 py-2 font-display text-[14px] font-medium border-b-2 transition',
                filter === tab.id
                  ? 'text-primary-600 border-primary-500'
                  : 'text-ink/60 border-transparent hover:text-ink hover:border-gray-300'
              )}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Category & Framework Filters */}
        <div className="flex items-center gap-3">
          <select 
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg font-display text-[14px]"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
          
          <select 
            value={frameworkFilter}
            onChange={(e) => setFrameworkFilter(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg font-display text-[14px]"
          >
            <option value="all">All Frameworks</option>
            {frameworks.map(fw => (
              <option key={fw} value={fw}>{fw}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Controls Table */}
      <div className="rounded-[10px] border border-gray-200 bg-white overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr className="font-display text-[12px] font-bold uppercase tracking-[0.04em] text-ink/55">
              <th className="text-left py-3 pr-4 pl-5">Code</th>
              <th className="text-left px-3 py-3">Control</th>
              <th className="text-left px-3 py-3">Category</th>
              <th className="text-left px-3 py-3">Severity</th>
              <th className="text-left px-3 py-3">Status</th>
              <th className="text-left px-3 py-3">Pass Rate</th>
              <th className="text-left px-3 py-3">Coverage</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {filteredControls.map(control => (
              <ControlRow
                key={control.id}
                control={control}
                onClick={() => navigate(`controls/${control.id}`)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {filteredControls.length === 0 && (
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Icon name="shield" size={48} className="text-ink/20 mb-4" />
          <h3 className="font-display font-semibold text-ink text-[18px] mb-2">
            No controls found
          </h3>
          <p className="font-display text-[14px] text-ink/60 mb-6 max-w-md">
            No controls match your current filter criteria. Try adjusting the filters or create a new control.
          </p>
          <button
            onClick={() => {
              setFilter('all');
              setCategoryFilter('all'); 
              setFrameworkFilter('all');
            }}
            className="px-4 py-2 bg-primary-500 text-white font-display font-semibold rounded-lg hover:bg-primary-600 transition"
          >
            Clear Filters
          </button>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-success-50 border border-success-200">
          <div className="font-display font-bold text-success-600 text-[24px]">
            {controls.filter(c => c.status === 'passing').length}
          </div>
          <div className="font-display text-[14px] text-success-600 font-medium">Passing Controls</div>
        </div>
        
        <div className="p-4 rounded-lg bg-danger-50 border border-danger-200">
          <div className="font-display font-bold text-danger-600 text-[24px]">
            {controls.filter(c => c.status === 'failing').length}
          </div>
          <div className="font-display text-[14px] text-danger-600 font-medium">Failing Controls</div>
        </div>
        
        <div className="p-4 rounded-lg bg-warning-50 border border-warning-200">
          <div className="font-display font-bold text-warning-600 text-[24px]">
            {controls.filter(c => c.evidence_required).length}
          </div>
          <div className="font-display text-[14px] text-warning-600 font-medium">Require Evidence</div>
        </div>
        
        <div className="p-4 rounded-lg bg-primary-50 border border-primary-200">
          <div className="font-display font-bold text-primary-600 text-[24px]">
            {Math.round(controls.reduce((sum, c) => sum + c.pass_rate, 0) / controls.length)}%
          </div>
          <div className="font-display text-[14px] text-primary-600 font-medium">Average Pass Rate</div>
        </div>
      </div>
    </div>
  );
}